'use client';

import { once } from 'lodash';
import { useEffect, useMemo } from 'react';

import { addErrorEventListener } from '@air/api';

import { useShopifyPluginLogout } from '~/hooks/useShopifyPluginLogout';
import { isUnauthenticatedApiError } from '~/utils/isUnauthenticatedApiError';

/**
 * Logs the user out when the Air API returns 401 (stale/expired session). Mirrors core's
 * `useHandleUnauthenticatedUser` / `useHandleSessionExpired` pattern so wedged Cognito state
 * cannot leave the embedded app on an infinite "Loading your workspace..." spinner.
 */
export const useHandleUnauthenticatedApiError = () => {
  const { logout } = useShopifyPluginLogout();

  const handleLogout = useMemo(() => once(() => logout()), [logout]);

  const removeListener = addErrorEventListener((error) => {
    if (isUnauthenticatedApiError(error)) {
      handleLogout();
    }
  });

  useEffect(() => {
    return () => {
      removeListener();
    };
  }, [removeListener]);
};
